"use client";

import { Icon } from "@iconify/react";
import { Button } from "@heroui/button";
import { Divider } from "@heroui/divider";
import { Input, Textarea } from "@heroui/input";
import { Pagination } from "@heroui/pagination";
import { Card, CardBody } from "@heroui/card";
import { Avatar } from "@heroui/avatar";

import useCommentSection from "./useCommentSection";

import { BlogComment, BlogCommentPayload } from "@/types";

const CommentSection = () => {
    const {
        submitComment,
        setCommentPayload,
        handleCommentPageChange,
        commentRef,
        comments,
        commentPayload,
        commentPending,
    } = useCommentSection();

    return (
        <>
            {/* COMMENT LIST */}
            <div ref={commentRef} className="mt-12">
                {comments?.data?.length > 0 ? (
                    <>
                        {/* HEADER */}
                        <div
                            className="flex items-center justify-between border-b border-divider pb-5 mb-10"
                            data-aos="fade-up"
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <Icon icon="solar:chat-round-dots-bold-duotone" width={20} />
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold tracking-tight">
                                        Discussion
                                    </h3>

                                    <p className="mt-1 text-sm text-default-500">
                                        {comments?.total ?? comments?.data?.length ?? 0}{" "}
                                        {(comments?.total ?? comments?.data?.length) === 1
                                            ? "comment"
                                            : "comments"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* COMMENTS */}
                        <div className="space-y-5">
                            {comments?.data?.map((comment: BlogComment, index: number) => (
                                <Card
                                    key={comment?._id}
                                    className="border border-default-100 bg-content1"
                                    data-aos="fade-up"
                                    data-aos-delay={Math.min(index * 75, 300)}
                                    radius="lg"
                                    shadow="sm"
                                >
                                    <CardBody className="p-5">
                                        <div className="flex gap-4">
                                            {/* AVATAR */}
                                            <Avatar
                                                showFallback
                                                classNames={{
                                                    base: "bg-primary/10 text-primary",
                                                }}
                                                name={comment?.name}
                                            />

                                            {/* CONTENT */}
                                            <div className="flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h4 className="text-sm font-semibold text-foreground">
                                                        {comment?.name}
                                                    </h4>

                                                    <span className="h-1 w-1 rounded-full bg-default-400" />

                                                    <div className="flex items-center gap-1 text-xs text-default-500">
                                                        <Icon icon="solar:calendar-linear" width={13} />

                                                        <time
                                                            dateTime={new Date(
                                                                comment?.createdAt,
                                                            ).toISOString()}
                                                        >
                                                            {new Date(comment?.createdAt).toLocaleDateString(
                                                                undefined,
                                                                {
                                                                    year: "numeric",
                                                                    month: "short",
                                                                    day: "numeric",
                                                                },
                                                            )}
                                                        </time>
                                                    </div>
                                                </div>

                                                <div className="mt-4 rounded-2xl bg-default-100/60 p-4">
                                                    <p className="whitespace-pre-wrap text-sm leading-7 text-default-700 dark:text-default-300">
                                                        {comment?.comment}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>

                        {/* PAGINATION */}
                        {comments?.totalPages > 1 && (
                            <div
                                className="mt-10 flex flex-col items-center gap-3"
                                data-aos="fade-up"
                            >
                                <Pagination
                                    showControls
                                    color="primary"
                                    page={comments?.page}
                                    total={comments?.totalPages}
                                    onChange={handleCommentPageChange}
                                />

                                <p className="text-sm text-default-500">
                                    Showing page {comments?.page} of {comments?.totalPages}
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <Card
                        className="border border-dashed border-default-200 bg-content1"
                        radius="lg"
                        shadow="sm"
                    >
                        <CardBody className="flex flex-col items-center justify-center py-16 px-6 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                                <Icon icon="solar:chat-round-dots-linear" width={26} />
                            </div>

                            <h3 className="mt-5 text-lg font-semibold">No comments yet</h3>

                            <p className="mt-2 max-w-sm text-sm text-default-500">
                                Be the first to share your thoughts on this article.
                            </p>
                        </CardBody>
                    </Card>
                )}
            </div>

            {/* COMMENT FORM */}
            <Card
                className="mt-20 border border-default-100 bg-content1"
                radius="lg"
                shadow="sm"
            >
                <CardBody className="p-6 md:p-8">
                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Icon icon="solar:pen-2-bold-duotone" width={20} />
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold">Leave a Comment</h3>

                            <p className="text-sm text-default-500">
                                Share your thoughts with the community
                            </p>
                        </div>
                    </div>

                    <Divider className="mb-6" />

                    <form className="space-y-5" onSubmit={submitComment}>
                        {/* NAME + EMAIL */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Input
                                isRequired
                                label="Your name"
                                labelPlacement="outside"
                                placeholder="Enter your name"
                                radius="lg"
                                size="lg"
                                value={commentPayload.name}
                                onValueChange={(value) =>
                                    setCommentPayload((prev: BlogCommentPayload) => ({
                                        ...prev,
                                        name: value,
                                    }))
                                }
                            />

                            <Input
                                isRequired
                                errorMessage="Please enter a valid email"
                                label="Your email"
                                labelPlacement="outside"
                                placeholder="john@example.com"
                                radius="lg"
                                size="lg"
                                type="email"
                                value={commentPayload.email}
                                onValueChange={(value) =>
                                    setCommentPayload((prev: BlogCommentPayload) => ({
                                        ...prev,
                                        email: value,
                                    }))
                                }
                            />
                        </div>

                        {/* COMMENT */}
                        <Textarea
                            disableAnimation
                            isRequired
                            label="Comment"
                            labelPlacement="outside"
                            minRows={6}
                            placeholder="Write your comment..."
                            radius="lg"
                            size="lg"
                            value={commentPayload.comment}
                            onValueChange={(value) =>
                                setCommentPayload((prev: BlogCommentPayload) => ({
                                    ...prev,
                                    comment: value,
                                }))
                            }
                        />

                        {/* BUTTON */}
                        <Button
                            className="font-medium"
                            color="primary"
                            isDisabled={commentPending}
                            isLoading={commentPending}
                            radius="lg"
                            size="lg"
                            startContent={
                                !commentPending && (
                                    <Icon icon="solar:plain-bold-duotone" width={18} />
                                )
                            }
                            type="submit"
                        >
                            {commentPending ? "Posting Comment..." : "Post Comment"}
                        </Button>
                    </form>
                </CardBody>
            </Card>
        </>
    );
};

export default CommentSection;
